import { CivilDate } from 'tc39-proposal-temporal';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { II18nService, IDateParts, IDatePicker, IComparedRange, IDateData } from './types';

export const getLastMonth = (date: CivilDate): CivilDate => {
  let lastMonthDate = date.plus({ months: -1 });
  if (lastMonthDate.day !== date.day) {
    lastMonthDate = lastMonthDate.plus({ days: -lastMonthDate.day });
  }
  return lastMonthDate;
};

export const formatCivilDate = ({
  i18n,
  date,
  dateFormat = 'M/d/yyyy',
}: {
  i18n: II18nService;
  date: CivilDate | null;
  dateFormat?: string;
}): string => `${date ? i18n.formatCivilDate(date, { raw: dateFormat }) : ''}`;

export const getDateObj = ({
  i18n,
  dateString,
  dateFormat,
}: {
  i18n: II18nService;
  dateString: string;
  dateFormat?: string;
}): IDateParts | null => {
  if (!dateString) { return null; }
  if (dateFormat) {
    return i18n.parseCivilDate(dateString, { raw: dateFormat }) as IDateParts;
  }

  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return (year && month && day) ? { year, month, day } : null;
};

export const parseCivilDate = ({
  i18n,
  dateString,
  dateFormat,
}: {
  i18n: II18nService;
  dateString: string;
  dateFormat?: string;
}): CivilDate | undefined => {
  const dateObj = getDateObj({ i18n, dateString, dateFormat });
  return dateObj ? new CivilDate(dateObj.year, dateObj.month, dateObj.day) : undefined;
};
export const translateLegacyToIANATimezone = (legacyTimeZone: string | number): string =>
  toIANATimeZone(fromLegacyTimeZone(legacyTimeZone as number)!);

export const generateDateData = (
  i18n: II18nService,
  dateFromDatePicker: IDatePicker,
  dateFormat: string,
  lcid: string | number
): IDateData => {
  const formattedStart = dateFromDatePicker.selectedComparedRange ? formatCivilDate({ i18n, date: dateFromDatePicker.selectedComparedRange.start, dateFormat }) : null;
  const formattedEnd = dateFromDatePicker.selectedComparedRange ? formatCivilDate({ i18n, date: dateFromDatePicker.selectedComparedRange.end, dateFormat }) : null;
  let compared: IComparedRange | null;
  if (!formattedStart && !formattedEnd) {
    compared = null;
  } else {
    compared = {
      formattedStart,
      formattedEnd,
      text: dateFromDatePicker.comparedOption && dateFromDatePicker.comparedOption.textToExport,
      key: dateFromDatePicker.comparedOption && dateFromDatePicker.comparedOption.keyToExport,
      start: formattedStart ? i18n.parseDate(formattedStart) : undefined,
      end: formattedEnd ? i18n.parseDate(formattedEnd) : undefined,
    };
  }
  const date = {
    RangeStartDate: formatCivilDate({ i18n, date: dateFromDatePicker.selectedRange.start, dateFormat }),
    RangeEndDate: formatCivilDate({ i18n, date: dateFromDatePicker.selectedRange.end, dateFormat }),
    RangeId: dateFromDatePicker.rangeType ? dateFromDatePicker.rangeType.rangeId : undefined,
    Rangelcid: lcid,
    compared,
    RangeIndex: dateFromDatePicker.rangeIndex,
    RangeDays: dateFromDatePicker.rangeDays,
  };
  return date;
};

export const getRangesFromData = (
  i18n: II18nService,
  dateData: IDateData | null,
  dateFormat: string,
  lcid: string | number
): {
  selectedRange?: { start?: CivilDate; end?: CivilDate };
  selectedComparedRange?: { start?: CivilDate; end?: CivilDate };
} => {
  if (!dateData || dateData.Rangelcid !== lcid) {
    return { selectedRange: undefined, selectedComparedRange: undefined };
  }
  const {
    RangeStartDate,
    RangeEndDate,
    compared,
  } = dateData;
  return {
    selectedRange: {
      start: RangeStartDate ? parseCivilDate({ i18n, dateString: RangeStartDate, dateFormat }) : undefined,
      end: RangeEndDate ? parseCivilDate({ i18n, dateString: RangeEndDate, dateFormat }) : undefined,
    },
    selectedComparedRange: {
      start: compared && compared.formattedStart ? parseCivilDate({ i18n, dateString: compared.formattedStart, dateFormat }) : undefined,
      end: compared && compared.formattedEnd ? parseCivilDate({ i18n, dateString: compared.formattedEnd, dateFormat }) : undefined,
    },
  };
};
